import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Send, 
  Users, 
  User, 
  MapPin, 
  Briefcase,
  AlertCircle,
  Smartphone,
  CheckCircle,
  Loader
} from 'lucide-react';
import { collection, getDocs, addDoc, serverTimestamp, writeBatch, doc, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { ToastContainer } from '../../../components/Toast';

interface NotificationForm {
  target: 'all' | 'specific' | 'city' | 'service';
  providerId?: string;
  city?: string;
  service?: string;
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high';
  screen: 'Dashboard' | 'Services' | 'Wallet' | 'Profile' | 'Notifications';
}

interface Provider {
  id: string;
  name: string;
  email: string;
  city?: string;
  selectedServices?: string[];
}

export default function NotificationsView() {
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const toast = useToast();

  const [formData, setFormData] = useState<NotificationForm>({
    target: 'all',
    title: '',
    message: '',
    priority: 'normal',
    screen: 'Dashboard'
  });

  const [selectedCount, setSelectedCount] = useState(0);

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    calculateSelectedCount();
  }, [formData, providers]);

  const loadProviders = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'serviceProviders'));
      const providersData = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || 'Unknown',
        email: doc.data().email || '',
        city: doc.data().city || doc.data().state || '',
        selectedServices: doc.data().selectedServices || []
      }));

      setProviders(providersData);

      // Extract unique cities
      const uniqueCities = [...new Set(providersData.map(p => p.city).filter(Boolean))];
      setCities(uniqueCities as string[]);

      // Extract unique services
      const allServices = providersData.flatMap(p => p.selectedServices || []);
      const uniqueServices = [...new Set(allServices)];
      setServices(uniqueServices as string[]);
    } catch (error) {
      console.error('Error loading providers:', error);
      toast.error('Failed to load providers');
    }
  };

  const calculateSelectedCount = () => {
    let count = 0;
    
    switch (formData.target) {
      case 'all':
        count = providers.length;
        break;
      case 'specific':
        count = formData.providerId ? 1 : 0;
        break;
      case 'city':
        if (formData.city) {
          count = providers.filter(p => p.city === formData.city).length;
        }
        break;
      case 'service':
        if (formData.service) {
          count = providers.filter(p => 
            p.selectedServices?.includes(formData.service!)
          ).length;
        }
        break;
    }
    
    setSelectedCount(count);
  };

  const getTargetProviders = (): string[] => {
    switch (formData.target) {
      case 'all':
        return providers.map(p => p.id);
      case 'specific':
        return formData.providerId ? [formData.providerId] : [];
      case 'city':
        return providers.filter(p => p.city === formData.city).map(p => p.id);
      case 'service':
        return providers.filter(p => 
          p.selectedServices?.includes(formData.service!)
        ).map(p => p.id);
      default:
        return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Message is required');
      return;
    }
    if (formData.target === 'specific' && !formData.providerId) {
      toast.error('Please select a provider');
      return;
    }
    if (formData.target === 'city' && !formData.city) {
      toast.error('Please select a city');
      return;
    }
    if (formData.target === 'service' && !formData.service) {
      toast.error('Please select a service');
      return;
    }

    setLoading(true);

    try {
      const targetProviderIds = getTargetProviders();

      if (targetProviderIds.length === 0) {
        toast.error('No providers match the selected criteria');
        setLoading(false);
        return;
      }

      // Use batch writes for multiple notifications (max 500 per batch)
      const batches: any[] = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;
      const MAX_BATCH_SIZE = 500;

      for (const providerId of targetProviderIds) {
        const notificationRef = doc(collection(db, 'providerNotifications'));
        
        currentBatch.set(notificationRef, {
          providerId,
          type: 'custom',
          title: formData.title.trim(),
          message: formData.message.trim(),
          priority: formData.priority,
          createdAt: serverTimestamp(),
          read: false,
          data: {
            screen: formData.screen,
            action: 'navigate'
          }
        });

        operationCount++;

        // If batch is full, save it and create a new one
        if (operationCount === MAX_BATCH_SIZE) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
          operationCount = 0;
        }
      }

      // Add remaining operations
      if (operationCount > 0) {
        batches.push(currentBatch);
      }

      // Commit all batches
      await Promise.all(batches.map(batch => batch.commit()));

      toast.success(`Successfully sent notification to ${targetProviderIds.length} provider(s)!`);
      
      // Reset form
      setFormData({
        target: 'all',
        title: '',
        message: '',
        priority: 'normal',
        screen: 'Dashboard'
      });
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Failed to send notifications');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const titleCharsLeft = 100 - formData.title.length;
  const messageCharsLeft = 500 - formData.message.length;

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-indigo-600 rounded-xl">
            <Bell className="h-8 w-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Send Notifications</h2>
            <p className="text-gray-600 mt-1">Send push notifications to service providers mobile app</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notification Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Target Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, target: 'all', providerId: undefined, city: undefined, service: undefined })}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.target === 'all'
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Users className="h-5 w-5" />
                    <span>All Providers</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, target: 'specific', city: undefined, service: undefined })}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.target === 'specific'
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="h-5 w-5" />
                    <span>Specific Provider</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, target: 'city', providerId: undefined, service: undefined })}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.target === 'city'
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <MapPin className="h-5 w-5" />
                    <span>By City</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, target: 'service', providerId: undefined, city: undefined })}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.target === 'service'
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Briefcase className="h-5 w-5" />
                    <span>By Service</span>
                  </button>
                </div>
              </div>

              {/* Conditional Filters */}
              {formData.target === 'specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Provider *
                  </label>
                  <select
                    value={formData.providerId || ''}
                    onChange={(e) => setFormData({ ...formData, providerId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Choose a provider...</option>
                    {providers.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} ({provider.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.target === 'city' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select City *
                  </label>
                  <select
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Choose a city...</option>
                    {cities.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.target === 'service' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Service Type *
                  </label>
                  <select
                    value={formData.service || ''}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Choose a service...</option>
                    {services.map(service => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value.slice(0, 100) })}
                  placeholder="Enter notification title..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  maxLength={100}
                />
                <p className={`text-xs mt-1 ${titleCharsLeft < 20 ? 'text-red-600' : 'text-gray-500'}`}>
                  {titleCharsLeft} characters remaining
                </p>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                  placeholder="Enter your message..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  required
                  maxLength={500}
                />
                <p className={`text-xs mt-1 ${messageCharsLeft < 50 ? 'text-red-600' : 'text-gray-500'}`}>
                  {messageCharsLeft} characters remaining
                </p>
              </div>

              {/* Priority & Screen */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Navigate To
                  </label>
                  <select
                    value={formData.screen}
                    onChange={(e) => setFormData({ ...formData, screen: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="Dashboard">Dashboard</option>
                    <option value="Services">Services</option>
                    <option value="Wallet">Wallet</option>
                    <option value="Profile">Profile</option>
                    <option value="Notifications">Notifications</option>
                  </select>
                </div>
              </div>

              {/* Target Summary */}
              {selectedCount > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-indigo-700">
                    <AlertCircle className="h-5 w-5" />
                    <p className="text-sm font-medium">
                      This notification will be sent to <span className="font-bold">{selectedCount}</span> provider{selectedCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || selectedCount === 0}
                className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Send Notification</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Live Preview */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Smartphone className="h-5 w-5 mr-2" />
              Live Preview
            </h3>

            {/* Mobile Notification Preview */}
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 rounded-2xl p-6 border-2 border-gray-300">
              <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
                <div className="flex items-start space-x-3">
                  {/* App Icon */}
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <Bell className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  {/* Notification Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-gray-900">RapsApp</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(formData.priority)}`}>
                        {formData.priority}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 mb-1 line-clamp-2">
                      {formData.title || 'Notification title will appear here'}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-3">
                      {formData.message || 'Your notification message will appear here. Type something to see the preview.'}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">Just now</p>
                  </div>
                </div>
              </div>

              {/* Preview Info */}
              <div className="mt-4 space-y-2 text-xs text-gray-600">
                <div className="flex items-center justify-between">
                  <span>Navigate to:</span>
                  <span className="font-medium text-gray-900">{formData.screen}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Target:</span>
                  <span className="font-medium text-gray-900">
                    {selectedCount} provider{selectedCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                Tips for Effective Notifications
              </h4>
              <ul className="text-xs text-amber-800 space-y-1">
                <li>• Keep titles short and actionable</li>
                <li>• Use clear, concise language</li>
                <li>• High priority for urgent updates only</li>
                <li>• Test with one provider first</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
